import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { PlusIcon } from "@heroicons/react/outline";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import { PlaceholderChildMessage } from "@/components/Playground/Messages/PlaceholderContainer/Content/ChildMessage";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import { DnDNestedContainer } from "@/components/Playground/Messages/PlaceholderContainer/dnd";
import { PlaceholderErrorBoundary } from "@/components/Playground/Messages/PlaceholderContainer/errors/ErrorBoundary";
import { MessageListFallback } from "@/components/Playground/Messages/PlaceholderContainer/errors/ErrorBoundary/Fallbacks";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PCCarouselItemProps {
  setId: string;
}

export const PCCarouselItem = observer(({ setId }: PCCarouselItemProps) => {
  const { readonly, childStoresMap, placeholderStore } = usePlaceholderContainer();
  const { activeSetId } = placeholderStore;
  const [isDragging, setIsDragging] = useState(false);

  // Get the childStore for this specific set
  const childStore = childStoresMap.get(setId);

  // Capture hydrationVersion so key changes force Tiptap editor remount on external edits
  const version = childStore?.hydrationVersion ?? 0;

  const renderChildMessage = (
    message: IMessageStore,
    index: number,
    dragHandleProps?: DraggableProvidedDragHandleProps,
  ) => (
    <PlaceholderChildMessage
      key={`${message.id}-${version}`}
      message={message}
      index={index}
      dragHandleProps={dragHandleProps}
    />
  );

  // Only render full content if this is the active set
  const isActive = setId === activeSetId;

  if (!childStore) {
    return <div className="py-4 text-center text-[13px] text-gray-400">Loading...</div>;
  }

  const handleAddChildMessage = () => {
    runInAction(() => {
      childStore.addMessage();
    });
  };

  const handleOnDragStart = () => {
    setIsDragging(true);
  };

  const handleOnDragEnd = () => {
    setIsDragging(false);
  };

  // Empty state: unified Notion-style block with text + inline action
  if (childStore.messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-5">
        <p className="text-[13px] leading-5 text-gray-400">
          Add test messages to preview what gets injected here at runtime.{" "}
          {!readonly && isActive && (
            <button
              type="button"
              onClick={handleAddChildMessage}
              className="inline-flex items-center text-gray-500 underline decoration-gray-300 underline-offset-2 transition-colors hover:text-gray-700 hover:decoration-gray-400"
            >
              Click to add
            </button>
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col",
        "transition-colors duration-200",
        isDragging && "border-blue-300",
      )}
    >
      <PlaceholderErrorBoundary fallback={<MessageListFallback />}>
        <DnDNestedContainer
          messages={childStore.messages}
          renderItem={renderChildMessage}
          onDragStart={handleOnDragStart}
          onDragEnd={handleOnDragEnd}
        />
      </PlaceholderErrorBoundary>

      {/* Add message button */}
      {!readonly && isActive && (
        <Button
          variant="outline"
          size="sm"
          className="mt-1.5 w-fit"
          onClick={handleAddChildMessage}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          Add Message
        </Button>
      )}
    </div>
  );
});
